/**
 * Página de Backup e Segurança
 * Nautilus OS Enterprise v3.18.0
 * Acesso: Apenas administradores
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Database, 
  Download, 
  RefreshCw, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  Cloud,
  CloudOff,
  Trash2,
  FileJson,
  Shield,
  Calendar,
  HardDrive,
  Loader2,
  Upload,
  Image,
  FileText,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/axios';
import { TitleWithHelp } from '../components/ui/help-tooltip';

// Configuração de ajuda do módulo
const HELP_CONFIG = {
  title: "Backup e Segurança",
  description: "Central de backup e armazenamento seguro. Gerencie backups automáticos do banco de dados e monitore o armazenamento de arquivos no AWS S3.",
  connections: [
    { module: "AWS S3", description: "Todos os arquivos (imagens, anexos, PDFs) são armazenados no S3 para garantir persistência e segurança." },
    { module: "MongoDB", description: "Backups automáticos do banco de dados são executados às 3h e 15h diariamente." },
    { module: "Projetos/Produtos", description: "Imagens e anexos enviados nesses módulos são armazenados via S3." }
  ],
  tips: [
    "Backups automáticos são executados às 3h e 15h.",
    "Arquivos no S3 são persistentes e não são perdidos em atualizações.",
    "Verifique se não há arquivos em 'Local (em risco)' - eles devem ser migrados.",
    "Use 'Exportar JSON' para baixar dados específicos manualmente."
  ]
};

const BackupPage = () => {
  const [status, setStatus] = useState(null);
  const [storageConfig, setStorageConfig] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executandoBackup, setExecutandoBackup] = useState(false);
  const [exportandoJson, setExportandoJson] = useState(false);
  const [restaurando, setRestaurando] = useState(null);
  const [migrando, setMigrando] = useState(false);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const [statusRes, historicoRes, storageRes] = await Promise.all([
        api.get('/backup/status'),
        api.get('/backup/historico?limite=20'),
        api.get('/storage/config')
      ]);
      setStatus(statusRes.data);
      setHistorico(historicoRes.data.backups || []);
      setStorageConfig(storageRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar informações de backup');
    } finally {
      setLoading(false);
    }
  };

  const executarBackupManual = async () => {
    setExecutandoBackup(true);
    try {
      await api.post('/backup/executar');
      toast.success('Backup iniciado! Acompanhe o progresso no histórico.');
      // Aguardar um pouco e recarregar
      setTimeout(() => {
        carregarDados();
        setExecutandoBackup(false);
      }, 5000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar backup');
      setExecutandoBackup(false);
    }
  };

  const exportarJson = async () => {
    setExportandoJson(true);
    try {
      const response = await api.post('/backup/exportar-json');
      
      // Criar arquivo para download
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nautilus_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exportação concluída! ${response.data.total_registros} registros exportados.`);
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao exportar dados');
    } finally {
      setExportandoJson(false);
    }
  };

  const restaurarBackup = async (backupId, nome) => {
    if (!window.confirm(`ATENÇÃO: Esta ação irá SOBRESCREVER todos os dados atuais!\n\nTem certeza que deseja restaurar o backup "${nome}"?`)) {
      return;
    }
    
    setRestaurando(backupId);
    try {
      const response = await api.post(`/backup/restaurar/${backupId}?confirmar=true`);
      toast.success(`Backup restaurado com sucesso! ${Object.keys(response.data.collections_restauradas).length} collections restauradas.`);
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao restaurar backup');
    } finally {
      setRestaurando(null);
    }
  };

  const deletarBackup = async (backupId, nome) => {
    if (!window.confirm(`Tem certeza que deseja excluir o backup "${nome}"?`)) {
      return;
    }
    
    try {
      await api.delete(`/backup/${backupId}`);
      toast.success('Backup removido');
      carregarDados();
    } catch (error) {
      toast.error('Erro ao remover backup');
    }
  };

  const migrarArquivosParaS3 = async () => {
    if (!window.confirm('Deseja verificar e migrar arquivos locais para o AWS S3?')) {
      return;
    }
    
    setMigrando(true);
    try {
      const response = await api.post('/storage/migrar-local-para-s3');
      
      if (response.data.migrados > 0) {
        toast.success(`${response.data.migrados} arquivos migrados para o S3 com sucesso!`);
      }
      
      if (response.data.arquivos_perdidos > 0) {
        // Mostrar detalhes dos arquivos perdidos
        const perdidos = response.data.detalhes_perdidos || [];
        toast.error(
          `ATENÇÃO: ${response.data.arquivos_perdidos} arquivo(s) NÃO FORAM ENCONTRADOS no servidor. ` +
          `Esses arquivos foram perdidos em deploys anteriores. Verifique o console (F12) para detalhes.`
        );
      }
      
      if (response.data.erros > 0) {
        toast.warning(`${response.data.erros} arquivos tiveram erros na migração.`);
      }
      
      if (response.data.migrados === 0 && response.data.arquivos_perdidos === 0) {
        toast.info('Nenhum arquivo local encontrado.');
      }
      
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar arquivos');
    } finally {
      setMigrando(false);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <TitleWithHelp
              title="Backup e Segurança"
              description="Central de backup e armazenamento seguro. Gerencie backups automáticos do banco de dados e monitore o armazenamento de arquivos no AWS S3."
              connections={['AWS S3', 'MongoDB', 'Projetos', 'Produtos']}
            >
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Shield className="h-6 w-6 text-blue-600" />
                Backup e Segurança
              </h1>
            </TitleWithHelp>
            <p className="text-gray-500 mt-1">Gerenciamento de backups e armazenamento</p>
          </div>
          <Button onClick={carregarDados} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Status do Backup */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-500">Status do Backup</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                {status?.backup_em_dia ? (
                  <>
                    <CheckCircle2 className="h-8 w-8 text-green-500" />
                    <div>
                      <p className="font-semibold text-green-700">Em dia</p>
                      <p className="text-sm text-gray-500">Último backup recente</p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-8 w-8 text-yellow-500" />
                    <div>
                      <p className="font-semibold text-yellow-700">Atenção</p>
                      <p className="text-sm text-gray-500">Backup atrasado</p>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Storage S3 */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-500">AWS S3</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                {status?.s3_configurado ? (
                  <>
                    <Cloud className="h-8 w-8 text-blue-500" />
                    <div>
                      <p className="font-semibold text-blue-700">Conectado</p>
                      <p className="text-sm text-gray-500 truncate max-w-[120px]">{status?.configuracao?.bucket}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <CloudOff className="h-8 w-8 text-gray-400" />
                    <div>
                      <p className="font-semibold text-gray-600">Não configurado</p>
                      <p className="text-sm text-gray-500">Aguardando chaves</p>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Total de Backups */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-500">Total de Backups</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Database className="h-8 w-8 text-purple-500" />
                <div>
                  <p className="font-semibold text-2xl">{status?.total_backups || 0}</p>
                  <p className="text-sm text-gray-500">Retenção: {status?.configuracao?.retencao_dias || 30} dias</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Arquivos no Storage */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-500">Arquivos Armazenados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <HardDrive className="h-8 w-8 text-indigo-500" />
                <div>
                  <p className="font-semibold text-2xl">{storageConfig?.estatisticas?.total_arquivos || 0}</p>
                  <p className="text-sm text-gray-500">
                    {storageConfig?.estatisticas?.arquivos_s3 || 0} S3 / {storageConfig?.estatisticas?.arquivos_local || 0} local
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alertas */}
        {!status?.s3_configurado && (
          <Alert className="bg-yellow-50 border-yellow-200">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              <strong>AWS S3 não configurado:</strong> Os backups estão sendo salvos localmente e podem ser perdidos em caso de deploy.
              Configure as variáveis <code className="bg-yellow-100 px-1 rounded">AWS_ACCESS_KEY_ID</code>, <code className="bg-yellow-100 px-1 rounded">AWS_SECRET_ACCESS_KEY</code> e <code className="bg-yellow-100 px-1 rounded">AWS_S3_BUCKET</code> no backend.
            </AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="backup" className="space-y-4">
          <TabsList>
            <TabsTrigger value="backup" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Backup MongoDB
            </TabsTrigger>
            <TabsTrigger value="storage" className="flex items-center gap-2">
              <Cloud className="h-4 w-4" />
              Storage S3
            </TabsTrigger>
          </TabsList>

          {/* Tab Backup */}
          <TabsContent value="backup" className="space-y-4">
            {/* Ações */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Ações de Backup</CardTitle>
                <CardDescription>Execute backup manual ou exporte dados</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-3">
                  <Button 
                    onClick={executarBackupManual} 
                    disabled={executandoBackup}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="btn-executar-backup"
                  >
                    {executandoBackup ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Database className="h-4 w-4 mr-2" />
                    )}
                    {executandoBackup ? 'Executando...' : 'Executar Backup Agora'}
                  </Button>
                  
                  <Button 
                    onClick={exportarJson} 
                    disabled={exportandoJson}
                    variant="outline"
                    data-testid="btn-exportar-json"
                  >
                    {exportandoJson ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileJson className="h-4 w-4 mr-2" />
                    )}
                    {exportandoJson ? 'Exportando...' : 'Exportar JSON'}
                  </Button>
                </div>
                
                <div className="text-sm text-gray-500 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>Backup automático: diariamente às 3h e 15h</span>
                </div>
              </CardContent>
            </Card>

            {/* Último Backup */}
            {status?.ultimo_backup && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    Último Backup Bem-Sucedido
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Data/Hora</p>
                      <p className="font-medium">{formatDate(status.ultimo_backup.criado_em)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Tipo</p>
                      <Badge variant={status.ultimo_backup.tipo === 'automatico' ? 'secondary' : 'default'}>
                        {status.ultimo_backup.tipo}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Tamanho</p>
                      <p className="font-medium">{formatBytes(status.ultimo_backup.tamanho_bytes)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Registros</p>
                      <p className="font-medium">{status.ultimo_backup.total_registros?.toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Histórico */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Histórico de Backups
                </CardTitle>
                <CardDescription>Últimos 20 backups realizados</CardDescription>
              </CardHeader>
              <CardContent>
                {historico.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Database className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>Nenhum backup realizado ainda</p>
                    <p className="text-sm">Execute o primeiro backup manualmente</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {historico.map((backup) => (
                      <div 
                        key={backup.id}
                        className="flex items-center justify-between p-3 rounded-lg border bg-gray-50 hover:bg-gray-100"
                        data-testid={`backup-item-${backup.id}`}
                      >
                        <div className="flex items-center gap-3">
                          {backup.status === 'concluido' ? (
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                          ) : backup.status === 'em_andamento' ? (
                            <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-500" />
                          )}
                          <div>
                            <p className="font-medium text-sm">{backup.nome}</p>
                            <p className="text-xs text-gray-500">
                              {formatDate(backup.criado_em)} • {backup.tipo}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm font-medium">{formatBytes(backup.tamanho_bytes)}</p>
                            <p className="text-xs text-gray-500">{backup.total_registros?.toLocaleString()} registros</p>
                          </div>
                          <Badge variant={
                            backup.status === 'concluido' ? 'success' : 
                            backup.status === 'em_andamento' ? 'secondary' : 'destructive'
                          }>
                            {backup.status === 'concluido' ? 'Concluído' :
                             backup.status === 'em_andamento' ? 'Em andamento' : 'Falhou'}
                          </Badge>
                          {backup.status === 'concluido' && backup.tipo !== 'export_json' && backup.tipo !== 'restauracao' && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-blue-500 hover:text-blue-700"
                                onClick={() => restaurarBackup(backup.id, backup.nome)}
                                disabled={restaurando === backup.id}
                                title="Restaurar backup"
                              >
                                {restaurando === backup.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <RotateCcw className="h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => deletarBackup(backup.id, backup.nome)}
                                title="Excluir backup"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Storage */}
          <TabsContent value="storage" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Cloud className="h-5 w-5" />
                  Configuração do Storage
                </CardTitle>
                <CardDescription>Armazenamento de arquivos (imagens, anexos)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-700">Status</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-500">AWS S3</span>
                        <Badge variant={storageConfig?.configurado ? 'success' : 'secondary'}>
                          {storageConfig?.configurado ? 'Configurado' : 'Não configurado'}
                        </Badge>
                      </div>
                      {storageConfig?.configurado && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Bucket</span>
                            <span className="font-medium">{storageConfig?.bucket}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Região</span>
                            <span className="font-medium">{storageConfig?.regiao}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-700">Limites</h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Image className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-500">Imagens</span>
                        </div>
                        <span className="font-medium">Máx. {storageConfig?.limites?.imagem_mb || 10}MB</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-500">Anexos</span>
                        </div>
                        <span className="font-medium">Máx. {storageConfig?.limites?.anexo_mb || 50}MB</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Upload className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-500">Compressão auto</span>
                        </div>
                        <span className="font-medium">&gt; {storageConfig?.limites?.compressao_threshold_mb || 1}MB</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Estatísticas de Arquivos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-3xl font-bold text-gray-700">{storageConfig?.estatisticas?.total_arquivos || 0}</p>
                    <p className="text-sm text-gray-500">Total de Arquivos</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-3xl font-bold text-blue-600">{storageConfig?.estatisticas?.arquivos_s3 || 0}</p>
                    <p className="text-sm text-gray-500">No AWS S3 (seguro)</p>
                  </div>
                  <div className={`p-4 rounded-lg ${storageConfig?.estatisticas?.arquivos_local > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                    <p className={`text-3xl font-bold ${storageConfig?.estatisticas?.arquivos_local > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {storageConfig?.estatisticas?.arquivos_local || 0}
                    </p>
                    <p className="text-sm text-gray-500">Local (em risco)</p>
                  </div>
                </div>
                
                {/* Alerta quando há arquivos locais */}
                {storageConfig?.estatisticas?.arquivos_local > 0 && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-red-800">
                          {storageConfig.estatisticas.arquivos_local} arquivo(s) em armazenamento local
                        </p>
                        {storageConfig.estatisticas.arquivos_local_perdidos > 0 ? (
                          <p className="text-sm text-red-600 mt-1">
                            <strong>⚠️ {storageConfig.estatisticas.arquivos_local_perdidos} arquivo(s) já foram PERDIDOS</strong> em deploys anteriores.
                            Os arquivos físicos não existem mais, apenas os registros no banco.
                          </p>
                        ) : (
                          <p className="text-sm text-red-600 mt-1">
                            Esses arquivos serão perdidos no próximo redeploy! Migre agora para o S3.
                          </p>
                        )}
                        
                        <div className="mt-3 flex gap-2">
                          <Button
                            onClick={migrarArquivosParaS3}
                            disabled={migrando}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="btn-migrar-s3"
                          >
                            {migrando ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Processando...
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4 mr-2" />
                                Verificar e Migrar
                              </>
                            )}
                          </Button>
                          
                          <Button
                            variant="outline"
                            onClick={async () => {
                              try {
                                const res = await api.get('/storage/arquivos-locais');
                                alert(`Total: ${res.data.total}\nDisponíveis: ${res.data.disponiveis}\nPerdidos: ${res.data.perdidos}\n\nVeja detalhes no console (F12)`);
                              } catch (err) {
                                toast.error('Erro ao buscar detalhes');
                              }
                            }}
                          >
                            Ver Detalhes
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Sucesso quando tudo está no S3 */}
                {storageConfig?.estatisticas?.arquivos_local === 0 && storageConfig?.estatisticas?.arquivos_s3 > 0 && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <p className="text-green-800">
                        Todos os arquivos estão armazenados com segurança no AWS S3.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {!storageConfig?.configurado && (
              <Card className="border-dashed border-2 border-gray-300">
                <CardContent className="py-8">
                  <div className="text-center space-y-4">
                    <CloudOff className="h-12 w-12 mx-auto text-gray-400" />
                    <div>
                      <h3 className="font-semibold text-gray-700">Configure o AWS S3</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Para armazenamento persistente de arquivos, configure as credenciais AWS no backend:
                      </p>
                    </div>
                    <div className="bg-gray-100 rounded-lg p-4 text-left max-w-md mx-auto font-mono text-sm">
                      <p>AWS_ACCESS_KEY_ID=sua_chave</p>
                      <p>AWS_SECRET_ACCESS_KEY=sua_secret</p>
                      <p>AWS_REGION=sa-east-1</p>
                      <p>AWS_S3_BUCKET=nautilus-files</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Configurações */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <HardDrive className="h-5 w-5" />
              Configurações Gerais
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Frequência de Backup</p>
                <p className="font-medium">Diário às 3:00 e 15:00</p>
              </div>
              <div>
                <p className="text-gray-500">Retenção</p>
                <p className="font-medium">{status?.configuracao?.retencao_dias || 30} dias</p>
              </div>
              <div>
                <p className="text-gray-500">Email de Alertas</p>
                <p className="font-medium">{status?.email_alertas || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500">Storage Principal</p>
                <p className="font-medium">
                  {status?.s3_configurado 
                    ? `AWS S3 (${status?.configuracao?.regiao})` 
                    : 'Local (não persistente)'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
  );
};

export default BackupPage;
